/**
 * Anatomy parser types and constants.
 *
 * Structural parsing is done server-side by Haiku for accuracy.
 * This module provides the shared types, color definitions, and
 * a converter from the API response to the ParseResult shape
 * used by the ZinsserAnatomyPanel component.
 */

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

export type GrammaticalRole =
  | 'subject'
  | 'verb'
  | 'object'
  | 'adjective'
  | 'adverb'
  | 'preposition'
  | 'conjunction'
  | 'determiner'
  | 'pronoun'
  | 'infinitive'

export const ALL_ROLES: GrammaticalRole[] = [
  'subject', 'verb', 'object', 'adjective', 'adverb',
  'preposition', 'conjunction', 'determiner', 'pronoun', 'infinitive',
]

export const DEFAULT_ACTIVE_ROLES: GrammaticalRole[] = [
  'subject', 'verb', 'object', 'adverb',
]

export interface SpanLabel {
  start: number
  end: number
  text: string
  role: GrammaticalRole
  detail?: string
  color: string
}

export interface SentenceStructure {
  text: string
  word_count: number
  opener_type: string
  voice: string
  verb_strength: string
  spans: SpanLabel[]
}

export interface ParagraphStructure {
  sentences: SentenceStructure[]
  rhythm: number[]
  opener_pattern: string[]
}

export interface ParseResult {
  paragraphs: ParagraphStructure[]
  content_hash: string
}

// ──────────────────────────────────────────────
// Role colors
// ──────────────────────────────────────────────

export const ROLE_COLORS: Record<GrammaticalRole, string> = {
  subject:      '#6B8ABA', // slate blue
  verb:         '#D4914E', // amber
  object:       '#6BA386', // sage green
  adjective:    '#C27BA0', // dusty rose
  adverb:       '#E07B5A', // terracotta
  preposition:  '#8B8B8B', // gray
  conjunction:  '#9B8EC4', // soft purple
  determiner:   '#A0A0A0', // light gray
  pronoun:      '#7EB5D6', // light blue
  infinitive:   '#B5985A', // muted gold
}

export const ROLE_LABELS: Record<GrammaticalRole, string> = {
  subject:      'Subject',
  verb:         'Verb',
  object:       'Object',
  adjective:    'Adj',
  adverb:       'Adv',
  preposition:  'Prep',
  conjunction:  'Conj',
  determiner:   'Det',
  pronoun:      'Pron',
  infinitive:   'Inf',
}

// ──────────────────────────────────────────────
// API response → ParseResult converter
// ──────────────────────────────────────────────

interface APIWordTag {
  text: string
  role: string
  detail?: string
}

interface APISentenceParse {
  text: string
  words: APIWordTag[]
  opener_type?: string
  voice?: string
  verb_strength?: string
}

interface APIParagraphParse {
  sentences: APISentenceParse[]
}

export interface APIParseResponse {
  paragraphs: APIParagraphParse[]
  content_hash: string
}

/**
 * Convert the API parse response (word-level tags) into the ParseResult shape
 * (character-offset spans) used by the ZinsserAnatomyPanel component.
 */
export function convertAPIResponse(apiResponse: APIParseResponse): ParseResult {
  const paragraphs: ParagraphStructure[] = []

  for (const apiPara of apiResponse.paragraphs) {
    const sentences: SentenceStructure[] = []

    for (const apiSent of apiPara.sentences) {
      const spans: SpanLabel[] = []
      const sentenceText = apiSent.text

      // Convert word tags to character-offset spans by finding each word in the sentence
      let searchFrom = 0
      for (const word of apiSent.words) {
        const idx = sentenceText.indexOf(word.text, searchFrom)
        if (idx === -1) continue

        const role = (ALL_ROLES.includes(word.role as GrammaticalRole)
          ? word.role
          : 'object') as GrammaticalRole

        spans.push({
          start: idx,
          end: idx + word.text.length,
          text: word.text,
          role,
          detail: word.detail,
          color: ROLE_COLORS[role],
        })

        searchFrom = idx + word.text.length
      }

      const wordCount = sentenceText.trim().split(/\s+/).filter(Boolean).length

      sentences.push({
        text: sentenceText,
        word_count: wordCount,
        opener_type: apiSent.opener_type || 'subject',
        voice: apiSent.voice || 'active',
        verb_strength: apiSent.verb_strength || 'standard',
        spans,
      })
    }

    if (sentences.length > 0) {
      paragraphs.push({
        sentences,
        rhythm: sentences.map(s => s.word_count),
        opener_pattern: sentences.map(s => s.opener_type),
      })
    }
  }

  return {
    paragraphs,
    content_hash: apiResponse.content_hash,
  }
}
